import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const EmployeeImportStep = ({ data, departmentInfo, onComplete, onBack, onCancel }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [importedData, setImportedData] = useState(data || []);
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importMethod, setImportMethod] = useState('csv'); // 'csv' or 'manual'

  const handleFileUpload = (e) => {
    const uploadedFile = e?.target?.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setFileName(uploadedFile?.name);
      setError('');
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      'First Name,Last Name,Email,Phone,Position,Employment Type,Citizen Status,Date of Birth,Hire Date',
      'John,Doe,john.doe@example.gov.pg,+675 XXX XXXX,HR Officer,full_time,citizen,1985-05-15,2020-01-15',
      'Jane,Smith,jane.smith@example.gov.pg,+675 XXX XXXX,Finance Manager,full_time,citizen,1982-08-22,2019-03-10'
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text?.split('\n')?.filter(line => line?.trim());
    if (lines?.length < 2) return [];

    const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
    const employees = [];

    for (let i = 1; i < lines?.length; i++) {
      const values = lines?.[i]?.split(',')?.map(v => v?.trim());
      if (values?.length >= 9) {
        employees?.push({
          firstName: values?.[0],
          lastName: values?.[1],
          email: values?.[2],
          phone: values?.[3],
          position: values?.[4],
          employmentType: values?.[5],
          citizenStatus: values?.[6],
          dateOfBirth: values?.[7],
          hireDate: values?.[8],
          department: departmentInfo?.departmentName || 'Unknown',
          employeeNumber: `EMP-${Date.now()}-${i}`,
          isValid: true,
          errors: []
        });
      }
    }

    return employees;
  };

  const validateEmployeeData = (employees) => {
    const errors = [];
    const validatedEmployees = employees?.map((emp, index) => {
      const empErrors = [];

      if (!emp?.firstName || emp?.firstName?.length < 2) {
        empErrors?.push('First name is required (min 2 characters)');
      }

      if (!emp?.lastName || emp?.lastName?.length < 2) {
        empErrors?.push('Last name is required (min 2 characters)');
      }

      if (!emp?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(emp?.email)) {
        empErrors?.push('Valid email is required');
      }

      if (!emp?.position) {
        empErrors?.push('Position is required');
      }

      if (!['full_time', 'part_time', 'contract', 'casual']?.includes(emp?.employmentType)) {
        empErrors?.push('Invalid employment type');
      }

      if (!['citizen', 'non_citizen']?.includes(emp?.citizenStatus)) {
        empErrors?.push('Invalid citizen status');
      }

      if (empErrors?.length > 0) {
        errors?.push({ row: index + 1, errors: empErrors });
      }

      return {
        ...emp,
        isValid: empErrors?.length === 0,
        errors: empErrors
      };
    });

    setValidationErrors(errors);
    return validatedEmployees;
  };

  const handleProcessFile = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e?.target?.result;
        const parsedEmployees = parseCSV(text);
        
        if (parsedEmployees?.length === 0) {
          setError('No valid employee data found in file');
          setLoading(false);
          return;
        }

        const validatedEmployees = validateEmployeeData(parsedEmployees);
        setImportedData(validatedEmployees);
        setLoading(false);
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setLoading(false);
      };

      reader?.readAsText(file);
    } catch (err) {
      setError(err?.message || 'Failed to process file');
      setLoading(false);
    }
  };

  const handleContinue = () => {
    const validEmployees = importedData?.filter(emp => emp?.isValid);
    
    if (validEmployees?.length === 0) {
      setError('Please fix validation errors before continuing');
      return;
    }

    onComplete?.(validEmployees);
  };

  const handleSkip = () => {
    onComplete?.([]);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Employee Import</h2>
        <p className="text-gray-600">
          Import employee data via CSV file or skip to add employees later.
        </p>
      </div>

      {/* Import Method Selection */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setImportMethod('csv')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importMethod === 'csv' ?'border-emerald-600 bg-emerald-50' :'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <Icon name="Upload" size={24} className="text-emerald-600 mb-2" />
            <p className="font-semibold text-gray-900">CSV Import</p>
            <p className="text-sm text-gray-600 mt-1">Upload employee data from CSV file</p>
          </button>

          <button
            onClick={() => setImportMethod('manual')}
            className={`p-4 rounded-lg border-2 transition-all ${
              importMethod === 'manual' ?'border-emerald-600 bg-emerald-50' :'border-gray-200 hover:border-emerald-300'
            }`}
          >
            <Icon name="UserPlus" size={24} className="text-emerald-600 mb-2" />
            <p className="font-semibold text-gray-900">Add Later</p>
            <p className="text-sm text-gray-600 mt-1">Skip and add employees manually</p>
          </button>
        </div>
      </div>

      {importMethod === 'csv' && (
        <>
          {/* CSV Template Download */}
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <Icon name="FileDown" size={20} className="text-amber-600 mr-3 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">Download CSV Template</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Use our template to ensure proper data formatting for PNG Employment Act compliance.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                iconName="Download"
              >
                Download
              </Button>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Employee Data (CSV)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-600 mb-2">
                  {fileName || 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500">CSV files only</p>
              </label>
            </div>
            {file && (
              <div className="mt-4">
                <Button
                  onClick={handleProcessFile}
                  loading={loading}
                  iconName="CheckCircle"
                  fullWidth
                >
                  Process File & Validate Data
                </Button>
              </div>
            )}
          </div>

          {/* Validation Errors */}
          {validationErrors?.length > 0 && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-800 mb-2">
                    {validationErrors?.length} Validation Error(s) Found
                  </p>
                  <div className="space-y-2">
                    {validationErrors?.slice(0, 5)?.map((error, index) => (
                      <div key={index} className="text-sm text-red-700">
                        <span className="font-semibold">Row {error?.row}:</span>{' '}
                        {error?.errors?.join(', ')}
                      </div>
                    ))}
                    {validationErrors?.length > 5 && (
                      <p className="text-sm text-red-600 italic">
                        ...and {validationErrors?.length - 5} more errors
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {importedData?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Employee Preview ({importedData?.length} records)
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Position</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importedData?.map((emp, index) => (
                        <tr key={index} className={emp?.isValid ? '' : 'bg-red-50'}>
                          <td className="px-4 py-3">
                            {emp?.isValid ? (
                              <Icon name="CheckCircle" size={16} className="text-emerald-600" />
                            ) : (
                              <Icon name="AlertCircle" size={16} className="text-red-600" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {emp?.firstName} {emp?.lastName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{emp?.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{emp?.position}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{emp?.employmentType}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {importMethod === 'manual' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <Icon name="Info" size={32} className="text-blue-600 mx-auto mb-3" />
          <p className="text-gray-700 mb-2">
            You can add employees manually after completing the setup wizard.
          </p>
          <p className="text-sm text-gray-600">
            Navigate to Employee Management to add individual employees or import data later.
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            iconName="ArrowLeft"
          >
            Back
          </Button>
          <Button
            variant="ghost"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
        <div className="flex gap-2">
          {importMethod === 'csv' && importedData?.length === 0 && (
            <Button
              variant="outline"
              onClick={handleSkip}
            >
              Skip for Now
            </Button>
          )}
          <Button
            onClick={handleContinue}
            iconName="ArrowRight"
            iconPosition="right"
            disabled={importMethod === 'csv' && importedData?.length === 0}
          >
            Continue to Compliance
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeImportStep;